var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { forwardRef, useEffect, useRef, useState, } from 'react';
import cn from 'classnames';
import { Icons } from '@beeline/design-tokens/js/iconfont';
import { RangeCalendar } from '../../Calendar';
import { EMPTY_DATE, RU_FORMAT_PATTERN } from '../../Calendar/constants';
import { TextField } from '../../TextField';
import { IconButton } from '../../IconButton';
import { DropdownMenu } from '../../DropdownMenu';
import { formatDateToISO, getInputValue, isEmptyDate, isFilledDate, ISO_FORMAT_PATTERN, maskDate, } from '../utils';
import { usePrevious } from '../hooks/use-previous';
import "./styles.css";
export var DatePickerRangeSplit = forwardRef(function (_a, forwardedRef) {
    var className = _a.className, _b = _a.value, value = _b === void 0 ? [] : _b, _c = _a.minDate, minDate = _c === void 0 ? '' : _c, _d = _a.maxDate, maxDate = _d === void 0 ? '' : _d, _e = _a.disabled, disabled = _e === void 0 ? false : _e, _f = _a.dataTestId, dataTestId = _f === void 0 ? 'datepicker' : _f, _g = _a.disabledStartDate, disabledStartDate = _g === void 0 ? false : _g, _h = _a.disabledEndDate, disabledEndDate = _h === void 0 ? false : _h, _j = _a.multiselect, multiselect = _j === void 0 ? false : _j, onFocus = _a.onFocus, onChange = _a.onChange, onBlur = _a.onBlur, onClickSelectDates = _a.onClickSelectDates, fullWidth = _a.fullWidth, mobileView = _a.mobileView, restProps = __rest(_a, ["className", "value", "minDate", "maxDate", "disabled", "dataTestId", "disabledStartDate", "disabledEndDate", "multiselect", "onFocus", "onChange", "onBlur", "onClickSelectDates", "fullWidth", "mobileView"]);
    var datepickerRef = useRef(null);
    var prevValue = usePrevious(value);
    var _k = useState(''), inputStartValue = _k[0], setInputStartValue = _k[1];
    var _l = useState(''), inputEndValue = _l[0], setInputEndValue = _l[1];
    var _m = useState(false), openedCalendar = _m[0], setOpenedCalendar = _m[1];
    useEffect(function () {
        if (multiselect) {
            setInputStartValue(getInputValue(minDate));
            setInputEndValue(getInputValue(maxDate));
            return;
        }
        var needCheckStartDate = !prevValue || isEmptyDate(prevValue);
        var isFilledDateValue = isFilledDate(value, {
            isPartial: needCheckStartDate,
        });
        if (isFilledDateValue) {
            var startDateValue = value[0], endDateValue = value[1];
            setInputStartValue(getInputValue(startDateValue));
            setInputEndValue(getInputValue(endDateValue));
        }
    }, []);
    useEffect(function () {
        if (openedCalendar && disabled) {
            setOpenedCalendar(false);
        }
    }, [disabled]);
    var handleChange = function (value) {
        onChange === null || onChange === void 0 ? void 0 : onChange(value);
    };
    var handleCalendarOpen = function () {
        setOpenedCalendar(true);
    };
    var handleCalendarClose = function () {
        if (onClickSelectDates)
            return;
        if (!mobileView)
            setOpenedCalendar(false);
    };
    var handleCalendarSelect = function (value) {
        if (multiselect) {
            handleChange(value);
            return;
        }
        var startDate = value[0], endDate = value[1];
        var isFilledValue = isFilledDate(value, {
            pattern: ISO_FORMAT_PATTERN,
        });
        var setDate = function (startDate, endDate) {
            var inputStartValue = getInputValue(startDate);
            var inputEndValue = getInputValue(endDate);
            setInputStartValue(inputStartValue);
            setInputEndValue(inputEndValue);
            handleChange([startDate, endDate]);
        };
        if (disabledStartDate) {
            var prevStartDate = (prevValue || value)[0];
            setDate(prevStartDate, isFilledValue ? endDate : startDate);
            handleCalendarClose();
            return;
        }
        if (disabledEndDate) {
            var _a = prevValue || value, _ = _a[0], prevEndDate = _a[1];
            setDate(startDate, prevEndDate);
            handleCalendarClose();
            return;
        }
        if (isFilledValue) {
            setDate(startDate, endDate);
            handleCalendarClose();
        }
        else {
            setDate(startDate, EMPTY_DATE);
            //setInputEndValue(EMPTY_DATE);
        }
    };
    var handleInputFocus = function (event) {
        if (!openedCalendar) {
            handleCalendarOpen();
        }
        onFocus === null || onFocus === void 0 ? void 0 : onFocus(event);
    };
    var handleInputStartDateChange = function (event) {
        var newInputValue;
        var newDateValue;
        var updatedInputValue = event.target.value;
        var maskedDate = maskDate(updatedInputValue);
        var newDate = isFilledDate(maskedDate, { pattern: RU_FORMAT_PATTERN })
            ? maskedDate
            : EMPTY_DATE;
        newInputValue = maskDate(updatedInputValue);
        newDateValue = newDate;
        setInputStartValue(newInputValue);
        setInputEndValue(EMPTY_DATE);
        handleChange([formatDateToISO(newDateValue), EMPTY_DATE]);
    };
    var handleInputEndDateChange = function (event) {
        var newInputValue;
        var newDateValue;
        var updatedInputValue = event.target.value;
        var maskedDate = maskDate(updatedInputValue);
        var newDate = isFilledDate(maskedDate, { pattern: RU_FORMAT_PATTERN })
            ? maskedDate
            : EMPTY_DATE;
        newInputValue = maskDate(updatedInputValue);
        newDateValue = newDate;
        setInputEndValue(newInputValue);
        handleChange([
            formatDateToISO(inputStartValue),
            formatDateToISO(newDateValue),
        ]);
    };
    var handleInputBlur = function (event) {
        onBlur === null || onBlur === void 0 ? void 0 : onBlur(event);
    };
    var handleCalendarIconClick = function () {
        if (openedCalendar) {
            handleCalendarClose();
        }
        else {
            handleCalendarOpen();
        }
    };
    var rightIcon = function (disabled) { return (React.createElement(IconButton, { size: 'medium', iconName: Icons.Calendar, onClick: !disabled ? handleCalendarIconClick : undefined, disabled: disabled })); };
    var dropdownMenuProps = {
        targetElement: datepickerRef,
        align: 'center',
        scrollable: false,
    };
    var saveDatesOnClick = onClickSelectDates
        ? function () {
            setOpenedCalendar(false);
            onClickSelectDates();
        }
        : undefined;
    return (React.createElement("div", { ref: datepickerRef, className: cn('lk-ui_datepicker lk-ui_datepicker-range-split', fullWidth && 'lk-ui_datepicker__full-width', mobileView && 'lk-ui_datepicker-range-split__mobile', className), "data-test-id": dataTestId },
        React.createElement("div", null,
            React.createElement(TextField, __assign({ ref: forwardedRef, value: inputStartValue, disabled: disabled || disabledStartDate || multiselect, endAdornment: rightIcon((!multiselect && disabled) || disabledStartDate), onBlur: handleInputBlur, onFocus: handleInputFocus, onChange: handleInputStartDateChange, fullWidth: fullWidth }, restProps)),
            !mobileView && React.createElement("span", null, "-"),
            React.createElement(TextField, __assign({ ref: forwardedRef, value: inputEndValue, disabled: disabled || disabledEndDate || multiselect, endAdornment: rightIcon((!multiselect && disabled) || disabledEndDate), onBlur: handleInputBlur, onFocus: handleInputFocus, onChange: handleInputEndDateChange, fullWidth: fullWidth }, restProps))),
        openedCalendar && (React.createElement(DropdownMenu, __assign({}, dropdownMenuProps),
            React.createElement(RangeCalendar, { value: value, minValue: minDate, maxValue: maxDate, disabledStartDate: disabledStartDate, disabledEndDate: disabledEndDate, datepickerRef: datepickerRef, onSelectDate: handleCalendarSelect, onOutsideClick: handleCalendarClose, dataTestId: dataTestId + "-calendar", multiselect: multiselect, onClickSelectDates: saveDatesOnClick, mobileView: mobileView })))));
});
